import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, RefreshCw, Cpu, MemoryStick, Thermometer, Plug, HardDrive, Network,
         Boxes, Clock, ShieldCheck, PackageSearch, TrendingUp, Loader2 } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import { ErrorConReintento } from '../ui/EstadoDePantalla';
import { etiquetaRol } from '../lib/roles';

/* =====================================================================
   Estado del servidor · carril ops (19 sep 2026)
   =====================================================================
   Un panel que se explica solo: cada tarjeta trae su valor grande, su
   semáforo y un párrafo que dice QUÉ significa y CUÁNDO preocuparse.
   Todo viene medido del anfitrión por el recolector (cada minuto) y de
   la API (tamaño de la base). Lo que no existe en este equipo se dice con
   esas palabras: nunca un cero ni un verde por omisión.
   ===================================================================== */

const COLOR = {
  VERDE: 'bg-green-50 border-green-200 text-green-800',
  AMARILLO: 'bg-amber-50 border-amber-200 text-amber-800',
  ROJO: 'bg-red-50 border-red-200 text-red-800',
  SIN_DATO: 'bg-gray-50 border-gray-200 text-gray-600',
  MEDIDO: 'bg-gray-50 border-gray-200 text-gray-700',
};
const PALABRA = { VERDE: 'Bien', AMARILLO: 'Atención', ROJO: 'Problema', SIN_DATO: 'Sin dato', MEDIDO: 'Medido' };

function gb(b) { return b == null ? '—' : `${(b / 1024 ** 3).toFixed(b >= 10 * 1024 ** 3 ? 0 : 1)} GB`; }
function mb(b) { return b == null ? '—' : b >= 1024 ** 3 ? gb(b) : `${Math.round(b / 1024 ** 2)} MB`; }
function bps(v) {
  if (v == null) return 'midiendo…';
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} Mb/s`;
  if (v >= 1e3) return `${Math.round(v / 1e3)} kb/s`;
  return `${Math.round(v)} b/s`;
}
function duracion(seg) {
  if (seg == null) return '—';
  const d = Math.floor(seg / 86400), h = Math.floor((seg % 86400) / 3600), m = Math.floor((seg % 3600) / 60);
  if (d > 0) return `${d} día${d === 1 ? '' : 's'} ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}
function hora(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
}

function Semaforo({ estado }) {
  const e = estado || 'SIN_DATO';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${COLOR[e] || COLOR.SIN_DATO}`}>
      <span aria-hidden="true">{e === 'VERDE' ? '●' : e === 'AMARILLO' ? '●' : e === 'ROJO' ? '●' : '○'}</span>
      {PALABRA[e] || e}
    </span>
  );
}

function Tarjeta({ icono: Icono, titulo, estado, valor, unidad, children, explica, cuando }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-2 min-w-0" aria-label={titulo}>
      <header className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold text-gray-900">
          {Icono && <Icono className="h-4 w-4 text-gray-500" aria-hidden="true" />}{titulo}
        </h3>
        <Semaforo estado={estado} />
      </header>
      {valor != null && (
        <p className="text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
          {valor}{unidad && <span className="text-base font-normal text-gray-500 ml-1">{unidad}</span>}
        </p>
      )}
      {children}
      {explica && <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Qué significa: </span>{explica}</p>}
      {cuando && <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Cuándo preocuparse: </span>{cuando}</p>}
    </section>
  );
}

function SinDato({ que, porque }) {
  return (
    <Tarjeta titulo={que} estado="SIN_DATO">
      <p className="text-sm text-gray-600">{porque || 'Este equipo no expone ese dato.'}</p>
    </Tarjeta>
  );
}

/* --- histórico: media y pico dibujados a mano (sin librerías) ---------- */
function Grafica({ titulo, serie, unidad, max: maxFijo, umbrales }) {
  const W = 640, H = 160, P = 28;
  const puntos = serie || [];
  if (!puntos.length) return <p className="text-sm text-gray-500">{titulo}: sin puntos en este rango.</p>;
  const max = maxFijo ?? Math.max(...puntos.map((p) => p.pico), 1) * 1.1;
  const x = (i) => P + (i * (W - 2 * P)) / Math.max(puntos.length - 1, 1);
  const y = (v) => H - P - (Math.min(v, max) / max) * (H - 2 * P);
  const linea = (k) => puntos.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p[k]).toFixed(1)}`).join(' ');
  const ultimo = puntos[puntos.length - 1];
  return (
    <figure className="min-w-0">
      <figcaption className="text-sm font-medium text-gray-800 flex flex-wrap gap-x-3">
        <span>{titulo}</span>
        <span className="text-gray-500 font-normal">media ahora {ultimo.media}{unidad} · pico {ultimo.pico}{unidad}</span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" role="img" aria-label={`${titulo}: media y pico`}>
        {umbrales?.map((u) => (
          <line key={u.v} x1={P} x2={W - P} y1={y(u.v)} y2={y(u.v)} stroke={u.color} strokeDasharray="4 4" strokeWidth="1" />
        ))}
        <line x1={P} x2={W - P} y1={H - P} y2={H - P} stroke="#e5e7eb" />
        <path d={linea('pico')} fill="none" stroke="#f59e0b" strokeWidth="1.5" opacity="0.8" />
        <path d={linea('media')} fill="none" stroke="#2563eb" strokeWidth="2" />
        <text x={P} y={12} fontSize="10" fill="#6b7280">{max.toFixed(unidad === '%' ? 0 : 1)}{unidad}</text>
        <text x={P} y={H - 8} fontSize="10" fill="#6b7280">{hora(puntos[0].t)}</text>
        <text x={W - P} y={H - 8} fontSize="10" fill="#6b7280" textAnchor="end">{hora(ultimo.t)}</text>
      </svg>
      <p className="text-xs text-gray-500"><span className="inline-block w-3 border-t-2 border-blue-600 align-middle mr-1" /> media <span className="inline-block w-3 border-t-2 border-amber-500 align-middle mx-1" /> pico</p>
    </figure>
  );
}

export default function EstadoDelServidor() {
  const [d, setD] = useState(null);
  const [h, setH] = useState(null);
  const [horas, setHoras] = useState(24);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [negocio, setNegocio] = useState(undefined);

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const [inst, hist] = await Promise.all([
        api.get('/ops/servidor'), api.get(`/ops/servidor/historico?horas=${horas}`),
      ]);
      setD(inst); setH(hist);
      // Lo del negocio solo si esta persona puede verlo (es el tablero del dueño);
      // si no, la tarjeta dice por qué no está, no un cero.
      try { setNegocio(await api.get('/finanzas/tablero')); } catch (e) { setNegocio({ sin: e?.status === 403 ? 'Solo lo ve el dueño de la empresa.' : 'No se pudo leer el tablero de finanzas.' }); }
    } catch (e) {
      setError(e?.message || 'No se pudo leer el estado del servidor.');
    } finally { setCargando(false); }
  }, [horas]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { const t = setInterval(cargar, 60000); return () => clearInterval(t); }, [cargar]);

  const problemas = d?.problemas || [];
  const lectura = d?.lectura;

  const grupos = useMemo(() => {
    if (!d || d.estado === 'SIN_DATO') return null;
    return d;
  }, [d]);

  if (error) return <ErrorConReintento que="No se pudo leer el estado del servidor" porque={error} onReintentar={cargar} ocupado={cargando} />;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><Activity className="h-5 w-5" aria-hidden="true" />Estado del servidor</h1>
          <Ayuda id="ops.servidor.pantalla" />
          <p className="text-sm text-gray-600">
            {d?.quien ? <>{d.quien.nombre} · {etiquetaRol(d.quien.rol)}</> : null}
            {lectura ? <> · última lectura {hora(lectura.momento)} ({lectura.dice})</> : null}
          </p>
        </div>
        <button type="button" onClick={cargar} disabled={cargando}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50 disabled:opacity-60">
          {cargando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
          Actualizar ahora
        </button>
      </header>

      {d?.estado === 'SIN_DATO' && (
        <div className="rounded-xl border border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
          <p className="font-medium">Sin lecturas.</p><p>{d.porque}</p>
        </div>
      )}

      {problemas.length > 0 ? (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4" role="alert">
          <p className="font-semibold text-red-800">{problemas.length} problema{problemas.length === 1 ? '' : 's'} que requiere{problemas.length === 1 ? '' : 'n'} atención</p>
          <ul className="mt-1 space-y-1 text-sm text-red-800">
            {problemas.map((p) => <li key={p.que}><span className="font-medium">{p.que}</span> · {p.porque}</li>)}
          </ul>
        </div>
      ) : grupos && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">Ningún problema que requiera atención en esta lectura.</div>
      )}

      {grupos && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {/* Procesador */}
          {grupos.procesador.estado === 'SIN_DATO' ? <SinDato que="Procesador" porque={grupos.procesador.porque} /> : (
            <Tarjeta icono={Cpu} titulo="Procesador" estado={grupos.procesador.estado}
                     valor={grupos.procesador.carga_por_nucleo} unidad="por núcleo"
                     explica={`Carga media de 1, 5 y 15 minutos: ${grupos.procesador.carga_1} · ${grupos.procesador.carga_5} · ${grupos.procesador.carga_15}, con ${grupos.procesador.nucleos} núcleos${grupos.procesador.frecuencia_mhz ? ` a ${grupos.procesador.frecuencia_mhz} MHz${grupos.procesador.frecuencia_max_mhz ? ` (máx. ${grupos.procesador.frecuencia_max_mhz})` : ''}` : ''}. «Carga por núcleo» es cuánto trabajo hay por cada núcleo: 1.0 es todos ocupados.`}
                     cuando="Por encima de 0.8 por núcleo durante rato, TEGO responde lento; por encima de 1.5, algo está consumiendo más de lo que hay y hay que mirar qué (Servicios, abajo)." />
          )}
          {/* Memoria */}
          {grupos.memoria.estado === 'SIN_DATO' ? <SinDato que="Memoria" porque={grupos.memoria.porque} /> : (
            <Tarjeta icono={MemoryStick} titulo="Memoria" estado={grupos.memoria.estado}
                     valor={`${grupos.memoria.usada_pct} %`}
                     explica={`Usada ${gb(grupos.memoria.usada_b)} de ${gb(grupos.memoria.total_b)}; disponible ${gb(grupos.memoria.disponible_b)}. Swap: ${mb(grupos.memoria.swap_usada_b)} de ${mb(grupos.memoria.swap_total_b)}. La memoria es donde trabajan la base y la aplicación; «disponible» es lo que se puede usar sin frenar nada.`}
                     cuando="Desde el 80 %, hay que mirar; al 92 % el sistema empieza a usar el disco como memoria (swap) y todo se vuelve lento; al 100 % mata procesos." />
          )}
          {/* Temperatura */}
          {grupos.temperatura.estado === 'SIN_DATO' ? <SinDato que="Temperatura" porque={grupos.temperatura.porque} /> : (
            <Tarjeta icono={Thermometer} titulo="Temperatura" estado={grupos.temperatura.estado}
                     valor={grupos.temperatura.grados} unidad="°C"
                     explica={`Se mide en el sensor «${grupos.temperatura.sensor}» del procesador (${grupos.temperatura.zonas.length} sensores en total). Verde por debajo de ${grupos.temperatura.umbral_amarillo} °C, amarillo hasta ${grupos.temperatura.umbral_rojo} °C, rojo desde ahí.`}
                     cuando={`Desde ${grupos.temperatura.umbral_rojo} °C el procesador se frena solo para no dañarse y TEGO va lento: revisa ventilación y polvo. Si sube sin carga, el ventilador puede estar fallando.`} />
          )}
          {/* Alimentación */}
          {grupos.alimentacion.estado === 'SIN_DATO' ? <SinDato que="Alimentación y frenado por calor" porque={grupos.alimentacion.porque} /> : (
            <Tarjeta icono={Plug} titulo="Alimentación y frenado por calor" estado={grupos.alimentacion.estado}
                     valor={grupos.alimentacion.corriente ? 'Con corriente' : 'Con batería'}>
              <ul className="text-sm text-gray-700">
                {grupos.alimentacion.fuentes.map((f) => (
                  <li key={f.nombre}>{f.tipo === 'Mains' ? `Toma de corriente: ${f.conectada ? 'conectada' : 'desconectada'}` : f.tipo === 'Battery' ? `Batería: ${f.carga_pct ?? '—'} % (${f.situacion || '—'})` : `${f.nombre}: ${f.tipo}`}</li>
                ))}
                <li>{grupos.alimentacion.throttling.estado === 'MEDIDO'
                  ? `Frecuencia ahora ${grupos.alimentacion.throttling.frecuencia_actual_mhz} MHz de ${grupos.alimentacion.throttling.frecuencia_max_mhz} máx.`
                  : `Frenado por calor: sin dato (${grupos.alimentacion.throttling.porque})`}</li>
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Qué significa: </span>{grupos.alimentacion.throttling.nota || 'Si el equipo va con batería, TEGO sigue mientras dure.'} Una frecuencia baja con el equipo descansando es normal (ahorra energía); baja con carga y temperatura altas es el equipo frenándose por calor.</p>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Cuándo preocuparse: </span>si dice «con batería»: se acabó la corriente y el equipo se apagará cuando ella se agote. Conéctalo.</p>
            </Tarjeta>
          )}
          {/* Almacenamiento */}
          {grupos.almacenamiento.estado === 'SIN_DATO' ? <SinDato que="Almacenamiento" porque={grupos.almacenamiento.porque} /> : (
            <Tarjeta icono={HardDrive} titulo="Almacenamiento" estado={grupos.almacenamiento.estado}
                     valor={`${grupos.almacenamiento.cifrado.usado_pct ?? grupos.almacenamiento.sistema.usado_pct} %`} unidad="usado">
              <ul className="text-sm text-gray-700 space-y-0.5">
                <li>Disco cifrado de TEGO: {gb(grupos.almacenamiento.cifrado.usado_b)} usados, {gb(grupos.almacenamiento.cifrado.libre_b)} libres de {gb(grupos.almacenamiento.cifrado.total_b)}</li>
                <li>Sistema: {gb(grupos.almacenamiento.sistema.usado_b)} usados, {gb(grupos.almacenamiento.sistema.libre_b)} libres</li>
                {Object.entries(grupos.almacenamiento.partes).map(([k, v]) => (
                  <li key={k} className="pl-3">· {({ base_de_datos: 'Base de datos', imagenes_y_archivos: 'Imágenes y archivos subidos', respaldos: 'Respaldos' })[k] || k}: {v.bytes != null ? mb(v.bytes) : `sin dato (${v.porque || v.nota})`}</li>
                ))}
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Qué significa: </span>cuánto del disco ocupa lo de TEGO. La base crece con cada movimiento; las imágenes con cada foto; los respaldos se rotan solos.</p>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Cuándo preocuparse: </span>al 80 % toca hacer sitio; al 90 % es urgente: si el disco se llena, la base de datos se detiene.</p>
            </Tarjeta>
          )}
          {/* Red */}
          {grupos.red.estado === 'SIN_DATO' ? <SinDato que="Red" porque={grupos.red.porque} /> : (
            <Tarjeta icono={Network} titulo="Red" estado="VERDE">
              <ul className="text-sm text-gray-700 space-y-1">
                {Object.entries(grupos.red.interfaces).map(([n, v]) => (
                  <li key={n}><span className="font-medium">{n}</span>{v.ip ? ` · ${v.ip}` : ' · sin dirección'}<br />
                    <span className="text-gray-600">↓ {bps(v.bajada_bps)} · ↑ {bps(v.subida_bps)} · total {gb(v.total_bajada_b)} bajados, {gb(v.total_subida_b)} subidos desde el arranque</span></li>
                ))}
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Qué significa: </span>lo que entra y sale por cada conexión ahora mismo (medido entre dos lecturas de un minuto) y lo acumulado. «eno1» es el cable de la planta; «tailscale0» el acceso remoto; el túnel a internet va por el cable.</p>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Cuándo preocuparse: </span>subida alta y sostenida sin que nadie esté usando TEGO merece una mirada a Servicios y al vigía de brechas.</p>
            </Tarjeta>
          )}
          {/* Servicios */}
          {grupos.servicios.estado === 'SIN_DATO' ? <SinDato que="Servicios" porque={grupos.servicios.porque} /> : (
            <Tarjeta icono={Boxes} titulo="Servicios" estado={grupos.servicios.estado}
                     valor={`${grupos.servicios.activos} de ${grupos.servicios.total}`} unidad="activos">
              <ul className="text-sm text-gray-700 space-y-0.5 max-h-56 overflow-auto">
                {grupos.servicios.filas.map((s) => (
                  <li key={s.nombre} className="flex flex-wrap gap-x-2">
                    <span aria-hidden="true">{s.activo ? '🟢' : '🔴'}</span>
                    <span className="font-medium">{s.que_hace}</span>
                    <span className="text-gray-500">desde {hora(s.desde)} · {s.critico ? 'si cae, TEGO se detiene' : 'si cae, el resto sigue'}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Qué significa: </span>cada pieza de TEGO corre en su propio contenedor; «desde» es la última vez que arrancó. Hay además {grupos.servicios.temporizadores_tego} tareas programadas (temporizadores).</p>
              <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Cuándo preocuparse: </span>un servicio marcado «si cae, TEGO se detiene» en rojo es una avería ahora mismo; uno que reinicia a cada rato (el «desde» cambia solo) también.</p>
            </Tarjeta>
          )}
          {/* Encendido y respaldo */}
          <Tarjeta icono={Clock} titulo="Tiempo encendido y respaldo" estado={grupos.respaldo.estado}
                   valor={duracion(grupos.encendido.equipo_segundos)} unidad="encendido">
            <ul className="text-sm text-gray-700 space-y-0.5">
              <li>Equipo encendido desde {hora(grupos.encendido.equipo_desde)}</li>
              <li>La aplicación (API) lleva {duracion(grupos.encendido.proceso_segundos)} corriendo (desde {hora(grupos.encendido.proceso_desde)})</li>
              <li className={grupos.respaldo.estado === 'ROJO' || grupos.respaldo.estado === 'SIN_DATO' ? 'text-red-700 font-medium' : ''}>
                Último respaldo: {grupos.respaldo.ultimo_exito ? `${hora(grupos.respaldo.ultimo_exito)} (hace ${grupos.respaldo.hace_horas} h)` : 'ninguno con éxito'} — {grupos.respaldo.dice || grupos.respaldo.porque}
              </li>
              {grupos.respaldo.automatico === false && <li className="text-red-700 font-medium">No hay respaldos automáticos: el temporizador diario no está activo.</li>}
              <li>Recolector del histórico: {grupos.historico?.estado === 'VERDE' ? `funcionando, ${grupos.historico.muestras} muestras guardadas (${grupos.historico.retencion_dias} días, ${grupos.historico.cada})` : `sin dato (${grupos.historico?.porque})`}</li>
            </ul>
            <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Qué significa: </span>si el equipo lleva poco encendido, hubo un reinicio (corte de luz, actualización o alguien lo apagó). El respaldo diario es la copia de la base que permite volver atrás.</p>
            <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Cuándo preocuparse: </span>un respaldo de más de 30 horas, o «ninguno», es un problema hoy, no mañana.</p>
          </Tarjeta>
          {/* Actualizaciones */}
          <Tarjeta icono={PackageSearch} titulo="Actualizaciones del sistema" estado={grupos.actualizaciones.estado}
                   valor={grupos.actualizaciones.pendientes} unidad="paquetes pendientes">
            <ul className="text-sm text-gray-700 space-y-0.5">
              <li>Ventana: {grupos.actualizaciones.ventana.cuando}</li>
              <li>Aviso previo: {grupos.actualizaciones.ventana.aviso_previo}</li>
              <li>Regla: {grupos.actualizaciones.ventana.regla} — hoy {grupos.actualizaciones.se_puede_actualizar_ahora ? 'SÍ hay respaldo fresco' : 'NO hay respaldo fresco: no actualizar a mano'}</li>
              <li>Automáticas: {grupos.actualizaciones.automaticas ? 'activas (parches de seguridad de Ubuntu)' : 'apagadas'}</li>
              <li>Última búsqueda de paquetes: {hora(grupos.actualizaciones.ultima_busqueda)}</li>
              <li>Última actualización: {grupos.actualizaciones.ultima ? `${grupos.actualizaciones.ultima.cuando} · ${grupos.actualizaciones.ultima.que || grupos.actualizaciones.ultima.orden}${grupos.actualizaciones.ultima.error ? ` · ERROR: ${grupos.actualizaciones.ultima.error}` : ' · bien'}` : 'sin registro'}</li>
            </ul>
            <div className="flex flex-wrap gap-2 pt-1">
              {[['Buscar ahora', 'buscar_ahora'], ['Ensayar el ciclo', 'ensayar'], ['Actualizar ahora', 'actualizar_ahora'], ['Saltar la próxima', 'saltar_proxima'], ['Ver registro', 'registro']].map(([et, k]) => (
                <button key={k} type="button" disabled title={grupos.actualizaciones.acciones[k]}
                        className="px-2.5 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-500 bg-gray-50 cursor-not-allowed">{et}</button>
              ))}
            </div>
            <p className="text-xs text-gray-600">Los botones están apagados porque {grupos.actualizaciones.nota_acciones}. Comandos: {Object.values(grupos.actualizaciones.acciones).map((a) => a.replace('SOLO_EL_DUENO: ', '')).join(' · ')}.</p>
            <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Cuándo preocuparse: </span>muchos paquetes pendientes durante semanas, o una última actualización con error.</p>
          </Tarjeta>
        </div>
      )}

      {/* Histórico */}
      <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" aria-label="Histórico">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-gray-500" aria-hidden="true" />Histórico</h2>
          <label className="text-sm text-gray-700">Rango{' '}
            <select value={horas} onChange={(e) => setHoras(Number(e.target.value))} className="ml-1 rounded-lg border border-gray-300 px-2 py-1 text-sm">
              <option value={6}>6 horas</option><option value={24}>24 horas</option><option value={72}>3 días</option><option value={168}>7 días</option>
            </select>
          </label>
        </header>
        {h?.estado === 'OK' ? (
          <>
            <p className="text-sm text-gray-600">{h.muestras} muestras en el rango (una por minuto; cada punto resume {h.paso_minutos} minutos). Se guardan {h.retencion_dias} días y lo más viejo se borra solo; el histórico empieza el {hora(h.desde)}.</p>
            <div className="grid gap-4 md:grid-cols-2">
              <Grafica titulo="Procesador (carga por núcleo)" serie={h.series.procesador} unidad="" umbrales={[{ v: 0.8, color: '#f59e0b' }, { v: 1.5, color: '#ef4444' }]} />
              <Grafica titulo="Temperatura" serie={h.series.temperatura} unidad="°C" max={100} umbrales={[{ v: 70, color: '#f59e0b' }, { v: 85, color: '#ef4444' }]} />
              <Grafica titulo="Memoria usada" serie={h.series.memoria} unidad="%" max={100} umbrales={[{ v: 80, color: '#f59e0b' }, { v: 92, color: '#ef4444' }]} />
              <Grafica titulo="Disco cifrado usado" serie={h.series.disco_cifrado} unidad="%" max={100} umbrales={[{ v: 80, color: '#f59e0b' }, { v: 90, color: '#ef4444' }]} />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed"><span className="font-medium text-gray-700">Por qué media y pico: </span>{h.nota}</p>
          </>
        ) : <p className="text-sm text-gray-600">{h?.porque || 'Cargando el histórico…'}</p>}
      </section>

      {/* Negocio */}
      <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-2" aria-label="Negocio">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gray-500" aria-hidden="true" />El negocio, con dato real</h2>
        {negocio === undefined ? <p className="text-sm text-gray-500">Cargando…</p> : negocio.sin ? (
          <p className="text-sm text-gray-600">Sin tarjetas de negocio aquí: {negocio.sin}</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[['Vendido', negocio.vendido], ['Cobrado', negocio.cobrado], ['Por cobrar', negocio.por_cobrar], ['Por pagar', negocio.por_pagar], ['En bancos', negocio.en_bancos]].map(([t, v]) => (
              <div key={t} className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">{t}</p>
                <p className="text-xl font-semibold tabular-nums">{v?.monto != null ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: negocio.moneda || 'MXN' }).format(v.monto) : 'sin dato'}</p>
                <p className="text-xs text-gray-500">{v?.dato_al ? `dato al ${hora(v.dato_al)}` : 'sin movimientos todavía'}</p>
              </div>
            ))}
            <div className="rounded-lg border border-dashed border-gray-300 p-3">
              <p className="text-xs text-gray-500">Impuestos por pagar</p>
              <p className="text-sm text-gray-600">Sin dato: TEGO no calcula impuestos por pagar en esta versión; cuando Finanzas lo tenga, aparece aquí.</p>
            </div>
          </div>
        )}
        {negocio && !negocio.sin && <p className="text-xs text-gray-500">Periodo {negocio.periodo?.desde} → {negocio.periodo?.hasta}. Cada cifra lleva la fecha del último dato que la forma.</p>}
      </section>
    </div>
  );
}
